import isEqual from 'lodash/isEqual';
import { CannotReadNextValueError } from './errors/CannotReadNextValueError';
import { NotEnoughColumnsError } from './errors/NotEnoughColumnsError';
import { UnexpectedColumnError } from './errors/UnexpectedColumnError';
import { ValueValidationError } from './errors/ValueValidationError';
const PROBABLY_INCOMPLETE_DATA = Symbol('PROBABLY_INCOMPLETE_DATA');
const CSV_END_OF_VALUE = 'CSV_END_OF_VALUE';
const CSV_END_OF_ROW = 'CSV_END_OF_ROW';
const readNextValue = (input, pos, { rowDelimiter, separator, quoteCharacter, quoteMode, escapeCharacter, isEndOfStream, }) => {
    const isAutoQuote = quoteMode === 'auto';
    const shouldBeQuoted = quoteMode === 'required';
    let isQuoted = shouldBeQuoted;
    let startPos = pos;
    if (startPos > input.length) {
        throw new Error('CSV fully read, nothing left');
    }
    if (shouldBeQuoted) {
        if (input[startPos] === quoteCharacter) {
            startPos += 1;
        }
        else if (input.length > 0) {
            throw new Error('Expected quoted CSV, but got unquoted');
        }
    }
    if (isAutoQuote) {
        if (input[startPos] === quoteCharacter) {
            startPos += 1;
            isQuoted = true;
        }
    }
    for (let i = startPos; i < input.length; i++) {
        const char = input[i];
        const nextCharIndex = i + 1;
        if (char === escapeCharacter) {
            // as next symbol escaped, we just skip it
            if (escapeCharacter !== quoteCharacter ||
                input[nextCharIndex] === escapeCharacter) {
                i = nextCharIndex;
                continue;
            }
        }
        if (isQuoted) {
            const nextCharAfterQuoteIndex = nextCharIndex + 1;
            if (char === quoteCharacter) {
                const nextChar = input[nextCharIndex];
                const endRowDelimiterIndex = nextCharIndex + rowDelimiter.length;
                if (nextCharIndex === input.length) {
                    if (isEndOfStream) {
                        return {
                            value: input.substring(startPos, i),
                            nextPos: nextCharAfterQuoteIndex,
                            type: CSV_END_OF_ROW,
                        };
                    }
                    return PROBABLY_INCOMPLETE_DATA;
                }
                else if (rowDelimiter.startsWith(nextChar) &&
                    input.substring(nextCharIndex, endRowDelimiterIndex) === rowDelimiter) {
                    return {
                        value: input.substring(startPos, i),
                        nextPos: endRowDelimiterIndex,
                        type: CSV_END_OF_ROW,
                    };
                }
                else if (nextChar === separator) {
                    return {
                        value: input.substring(startPos, i),
                        nextPos: nextCharAfterQuoteIndex,
                        type: CSV_END_OF_VALUE,
                    };
                }
                throw new Error('CSV quote is ended, but no separator found');
            }
            else {
                continue;
            }
        }
        if (char === separator) {
            return {
                value: input.substring(startPos, i),
                nextPos: nextCharIndex,
                type: CSV_END_OF_VALUE,
            };
        }
        else if (rowDelimiter.startsWith(char) &&
            input.substring(i, i + rowDelimiter.length) === rowDelimiter) {
            return {
                value: input.substring(startPos, i),
                nextPos: i + rowDelimiter.length,
                type: CSV_END_OF_ROW,
            };
        }
    }
    if (isEndOfStream) {
        if (isQuoted) {
            throw new Error('CSV quoted value has no enclosed quote');
        }
        return {
            value: input.substring(startPos),
            nextPos: input.length + 1,
            type: CSV_END_OF_ROW,
        };
    }
    return PROBABLY_INCOMPLETE_DATA;
};
const readValues = (input, initialPos, options, rowPos) => {
    const expectedLength = options.expectedLength;
    let startPos = initialPos;
    const values = new Array(expectedLength);
    for (let i = 0; i < expectedLength; i++) {
        const isLast = expectedLength - 1 === i;
        const result = readNextValue(input, startPos, options);
        if (result === PROBABLY_INCOMPLETE_DATA) {
            return PROBABLY_INCOMPLETE_DATA;
        }
        else if (result.type === CSV_END_OF_VALUE) {
            if (!isLast) {
                values[i] = result.value;
                startPos = result.nextPos;
            }
            else {
                throw new UnexpectedColumnError(expectedLength, rowPos);
            }
        }
        else if (result.type === CSV_END_OF_ROW) {
            if (isLast) {
                values[i] = result.value;
                startPos = result.nextPos;
            }
            else {
                throw new NotEnoughColumnsError(expectedLength, i + 1, rowPos);
            }
        }
    }
    return { values, nextPos: startPos };
};
const MAX_FORMATTED_HEADER_LENGTH = 100;
const formatHeaderValues = (values) => {
    let valuesDetails = JSON.stringify(values);
    if (valuesDetails.length > MAX_FORMATTED_HEADER_LENGTH) {
        valuesDetails =
            valuesDetails.substring(0, MAX_FORMATTED_HEADER_LENGTH) + '...]';
    }
    return valuesDetails;
};
const lookupForEndOfRow = (input, { quoteMode, quoteCharacter, escapeCharacter }) => {
    let isQuoted = false;
    for (let i = 0; i < input.length; i++) {
        const char = input[i];
        if (isQuoted && char === escapeCharacter) {
            continue;
        }
        if (quoteMode !== 'none') {
            if (char === quoteCharacter) {
                isQuoted = !isQuoted;
            }
        }
        if (!isQuoted) {
            if (char === `\n`) {
                if (input[i - 1] === `\r`) {
                    return `\r\n`;
                }
                return char;
            }
        }
    }
    return '\n';
};
export const buildCsvValidator = (schema, { errorExtractor = (err) => [err ? err.toString() : 'Unknown'], rowDelimiter = 'auto', separator = ',', quoteMode = 'auto', quoteCharacter = `"`, escapeCharacter = '"', maxChunksAllowedBeforeFail = 2, caseInsensitiveHeader = false, validationMode = 'stopOnFirstFailure', } = {}) => {
    let isHeader = !Array.isArray(schema);
    const valuesSchema = Object.values(schema);
    const expectedLength = valuesSchema.length;
    const isStopOnFirstFailure = validationMode === 'stopOnFirstFailure';
    const valueReadOptions = {
        rowDelimiter,
        separator,
        quoteMode,
        quoteCharacter,
        expectedLength,
        escapeCharacter,
    };
    let lastChunkData = '';
    let rowScanned = 0;
    let chunkWithoutCompleteValues = 0;
    return ({ value, done, }) => {
        let startPos = 0;
        const errors = [];
        const csvValidatorInfo = {
            errors,
        };
        if (rowScanned === 0 && value && rowDelimiter === 'auto') {
            valueReadOptions.rowDelimiter = lookupForEndOfRow(value, valueReadOptions);
        }
        if (done && !value) {
            if (!lastChunkData) {
                return {
                    errors,
                };
            }
        }
        const input = lastChunkData + value;
        let result;
        valueReadOptions.isEndOfStream = done;
        while ((result = readValues(input, startPos, valueReadOptions, rowScanned))) {
            if (result === PROBABLY_INCOMPLETE_DATA) {
                if (chunkWithoutCompleteValues === maxChunksAllowedBeforeFail) {
                    throw new CannotReadNextValueError(valueReadOptions.rowDelimiter, input.length - startPos);
                }
                if (done) {
                    throw new Error('Stream end, but cannot properly parse');
                }
                else {
                    lastChunkData = input.substring(startPos);
                    chunkWithoutCompleteValues += 1;
                    return csvValidatorInfo;
                }
            }
            rowScanned += 1;
            chunkWithoutCompleteValues = 0;
            const values = result.values;
            if (isHeader) {
                let expectedHeaderValues = Object.keys(schema);
                let actualHeaderValues = values;
                if (caseInsensitiveHeader) {
                    expectedHeaderValues = expectedHeaderValues.map((header) => header.toLowerCase());
                    actualHeaderValues = actualHeaderValues.map((header) => header.toLowerCase());
                }
                if (!isEqual(actualHeaderValues, expectedHeaderValues)) {
                    throw new Error('Unexpected column headers, should be ' +
                        formatHeaderValues(expectedHeaderValues) +
                        ', but got ' +
                        formatHeaderValues(actualHeaderValues));
                }
                isHeader = false;
            }
            else {
                // validate values
                if (valuesSchema.length !== values.length) {
                    throw new NotEnoughColumnsError(valuesSchema.length, values.length, rowScanned);
                }
                for (let i = 0; i < valuesSchema.length; i++) {
                    try {
                        valuesSchema[i](values[i]);
                    }
                    catch (err) {
                        const validationError = new ValueValidationError(values[i], errorExtractor(err), values, rowScanned);
                        if (isStopOnFirstFailure) {
                            throw validationError;
                        }
                        else {
                            errors.push(validationError);
                        }
                    }
                }
            }
            startPos = result.nextPos;
            if (startPos >= input.length) {
                // clean lastChunkData as it may prepend to the next chunk wrong data
                lastChunkData = '';
                return csvValidatorInfo;
            }
        }
        return csvValidatorInfo;
    };
};
export const createValidateCsvStream = ({ brakeFn, }) => {
    return (stream, validator, { maxErrorsCount = 100 } = {}) => {
        const events = new EventTarget();
        let errors = [];
        let totalErrorsCount = 0;
        const whenComplete = new Promise((resolve, reject) => {
            const process = async () => {
                const reader = stream.getReader();
                let result;
                let proceed = 0;
                while ((result = await reader.read())) {
                    const validatorResult = validator({
                        done: result.done,
                        value: result.value ?? '',
                    });
                    totalErrorsCount += validatorResult.errors.length;
                    if (errors.length < maxErrorsCount) {
                        errors = errors
                            .concat(validatorResult.errors)
                            .slice(0, maxErrorsCount);
                    }
                    if (result.done) {
                        return Promise.resolve({
                            isValid: errors.length === 0,
                            proceed,
                            totalErrorsCount,
                            hasMoreErrorsThanTheLimit: totalErrorsCount > errors.length,
                            errorSamples: errors,
                        });
                    }
                    proceed += result.value?.length ?? 0;
                    await new Promise((resolve) => {
                        events.dispatchEvent(new CustomEvent('progress', {
                            detail: {
                                isValid: errors.length === 0,
                                proceed,
                                totalErrorsCount,
                                hasMoreErrorsThanTheLimit: totalErrorsCount > errors.length,
                                errorSamples: errors,
                            },
                        }));
                        brakeFn(resolve);
                    });
                }
                throw new Error('Unreachable code');
            };
            process().then(resolve, reject);
        });
        return {
            events,
            whenComplete,
        };
    };
};
//# sourceMappingURL=streamCsvValidation.js.map